<script lang="ts">
	import { browser } from '$app/environment';
	import { afterNavigate, beforeNavigate } from '$app/navigation';
	import { base } from '$app/paths';
	import { page } from '$app/stores';
	import { PublicNavigation } from '@infinitro/analytics';
	import { Background } from '@infinitro/background';
	import { BetBy } from '@infinitro/betby';
	import { initGamesState } from '@infinitro/catalog-ex';
	import { getProviders } from '@infinitro/catalog-min';
	import {
		cfgAppVersion,
		cfgBetbyScriptUrl,
		cfgBrandTitle,
		cfgCanonicalUrl,
		cfgEventThemeCdnPath,
		cfgLiveChatLicenseId,
		cfgNoIndex,
		cfgOptimoveTenantId,
		cfgOptimoveTenantToken,
		FeatureSet,
		FLUSH_HREFLANG_ROUTES,
		getThemeSetting,
		isEventAssetEnabled,
		ReleaseStage,
		SEO_CANONICAL,
		stateFeatureSet,
		stateHasSports,
		stateRealm,
		stateTheme,
		stateUi,
		wantAtLeastReleaseStage
	} from '@infinitro/config';
	import { seoContent, seoContentReady } from '@infinitro/content';
	import { CookiesModal } from '@infinitro/cookies';
	import { Footer } from '@infinitro/footer';
	import {
		Fonts,
		handleCustomerSupport,
		handleModalRouteNavigation,
		handlePublicToPlayerRedirect,
		hasSportsLoaded,
		initModalRouteDismissSync,
		isBetbyDisplayed,
		isFullScreenGame,
		isMobile,
		isPlayerChatOpen,
		loggedIn,
		ModalDialog,
		selectedItemMobileMenu,
		sidebarCollapsed,
		stateIFrameHref,
		stateOpenModal,
		stateShowSidebarMobile,
		syncLocaleFromPathname
	} from '@infinitro/global';
	import { Header } from '@infinitro/header-public';
	import { Iframe } from '@infinitro/iframe';
	import { MobileMenu } from '@infinitro/mobile-menu';
	import {
		setProviders,
		setTitleAndDescriptionForSEO,
		updateCanonicalUrl,
		updateRobots
	} from '@infinitro/seo';
	import { SideBar } from '@infinitro/sidebar';
	import { SupportChat } from '@infinitro/support-chat';
	import { ToastNotifications } from '@infinitro/toast-notifications';
	import { stateVisit } from '@infinitro/visit';
	import { onMount } from 'svelte';
	import { get } from 'svelte/store';
	import '../app.postcss';
	import Modals from './modals.svelte';
	let realm: string;
	stateRealm.subscribe((value) => (realm = value));

	const APP_VERSION = cfgAppVersion();
	const BETBY_SCRIPT_URL = cfgBetbyScriptUrl();
	const CANONICAL_URL = cfgCanonicalUrl();
	const BRAND_TITLE = cfgBrandTitle();
	const LANGUAGE_DEFAULT = getThemeSetting().languageDefault;

	const webSiteOrganizationLdJson = (): string =>
		JSON.stringify({
			'@context': 'https://schema.org',
			'@type': 'WebSite',
			name: cfgBrandTitle(),
			url: CANONICAL_URL
		});
	// Also noindex /ko/* on fomo.io - Korean content lives on kr.fomo.io subdomain
	$: NO_INDEX =
		cfgNoIndex() || (CANONICAL_URL === SEO_CANONICAL.fomo && $page.url.pathname.startsWith('/ko/'));
	// Locale-prefixed game/provider pages - crawlable but noindex'd to avoid duplicate CSR shells.
	// Sports locale pages are excluded: static shells are generated per locale and are indexable.
	$: isLocaleGamePage =
		/^\/(?:en|cs|de|es|fr|it|ja|ko|pt|ru|zh)\/(?:p|providers)\//.test($page.url.pathname) ||
		/^\/v1(\/|$)/.test($page.url.pathname);

	// Routes shared between fomo.io and kr.fomo.io - require hreflang alternates
	const HREFLANG_GLOBAL = SEO_CANONICAL.fomo;
	const HREFLANG_KR = SEO_CANONICAL.krFomo;
	// From central FLUSH_HREFLANG_ROUTES (@infinitro/config), with trailing slashes for this brand.
	const HREFLANG_ROUTES = new Set(
		FLUSH_HREFLANG_ROUTES.map((route) => (route === '/' ? '/' : `${route}/`))
	);
	const hreflangEnabled = CANONICAL_URL === HREFLANG_GLOBAL || CANONICAL_URL === HREFLANG_KR;
	const OG_LOCALE_MAP: Record<string, string> = {
		de: 'de_DE',
		es: 'es_ES',
		fr: 'fr_FR',
		it: 'it_IT',
		pt: 'pt_BR',
		ru: 'ru_RU',
		tr: 'tr_TR',
		cs: 'cs_CZ',
		ja: 'ja_JP',
		ko: 'ko_KR',
		zh: 'zh_CN'
	};

	console.log(`public website version ${APP_VERSION}`);

	let showSidebarMobile: boolean = false;
	let playerChatOpen: boolean = false;

	const setAppViewportHeight = () => {
		const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
		const next = `${viewportHeight}px`;
		const prev = document.documentElement.style.getPropertyValue('--app-vh').trim();
		if (prev === next) return;
		document.documentElement.style.setProperty('--app-vh', next);
	};

	const blurActiveEditable = () => {
		const activeElement = document.activeElement;
		if (!(activeElement instanceof HTMLElement)) return;
		const tag = activeElement.tagName;
		const isEditable =
			activeElement.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
		if (isEditable) activeElement.blur();
	};

	const waitForKeyboardToSettle = async () => {
		const vv = window.visualViewport;
		const baselineHeight = window.innerHeight;
		const getViewportHeight = () => vv?.height ?? window.innerHeight;
		const sleepFrame = () => new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

		blurActiveEditable();
		setAppViewportHeight();

		if (!vv) {
			await sleepFrame();
			setAppViewportHeight();
			return;
		}

		const maxDeadline = performance.now() + 450;
		let previousHeight = getViewportHeight();

		while (performance.now() < maxDeadline) {
			await sleepFrame();
			const currentHeight = getViewportHeight();
			const keyboardLikelyOpen = currentHeight < baselineHeight - 120;
			const hasStabilized = Math.abs(currentHeight - previousHeight) < 1;
			setAppViewportHeight();
			if (!keyboardLikelyOpen && hasStabilized) break;
			previousHeight = currentHeight;
		}

		setAppViewportHeight();
		setTimeout(setAppViewportHeight, 120);
	};

	stateShowSidebarMobile.subscribe((value) => (showSidebarMobile = value));
	isPlayerChatOpen.subscribe((value) => (playerChatOpen = value));
	stateVisit.subscribe((value) => {
		if (value) console.log(`visit ${value}`);
	});

	let isLoggedIn: boolean | undefined = undefined;
	loggedIn.subscribe((value) => {
		if (value === isLoggedIn) return;
		isLoggedIn = value;
		if (isLoggedIn) {
			handlePublicToPlayerRedirect();
		}

		initGamesState(value ?? false, { realm });
	});

	const onCollapseSidebar = () => {
		if (window.innerWidth < 1024) return;
		sidebarCollapsed.update((value) => !value);
	};

	const onShowSidebar = () => {
		const shouldOpen = !showSidebarMobile;

		if (shouldOpen) {
			isPlayerChatOpen.set(false);
			stateShowSidebarMobile.set(true);
			waitForKeyboardToSettle().then(() => {
				if (!showSidebarMobile) return;
				window.scrollTo(0, 0);
				document.documentElement.scrollTop = 0;
				document.body.scrollTop = 0;
				innerPageElement?.scrollTo({ top: 0, behavior: 'auto' });
			});
		} else {
			stateShowSidebarMobile.set(false);
		}
	};

	const onShowPlayerChat = () => {
		isPlayerChatOpen.set(!playerChatOpen);
	};

	const onShowSearchModal = () => {
		stateOpenModal.set(ModalDialog.Search);
	};

	const onMobileMenuChatSlotClick = () => {
		if ($isMobile && $isFullScreenGame && $stateUi.games.showNavigationOnMobile) {
			isPlayerChatOpen.set(false);
			stateShowSidebarMobile.set(false);
			return;
		}
		onShowPlayerChat();
	};

	let innerPageElement: HTMLElement;

	// The shell injects an sr-only <h1 id="seo-h1"> so crawlers that skip hidden content still see a
	// heading; it must go once the rendered page supplies its own. A one-shot check is not enough:
	// Helpdesk and SeoText fetch their markdown from the CDN, so their h1 lands well after onMount
	// and both headings survive (/bonus-terms/, /terms-sports/ and their locale copies).
	const SEO_H1_WATCH_MS = 15_000;
	let seoH1Observer: MutationObserver | null = null;
	let seoH1WatchTimer: ReturnType<typeof setTimeout> | null = null;

	const stopSeoH1Watch = () => {
		seoH1Observer?.disconnect();
		seoH1Observer = null;
		if (seoH1WatchTimer) clearTimeout(seoH1WatchTimer);
		seoH1WatchTimer = null;
	};

	const pruneSeoH1 = () => {
		const seoH1 = document.getElementById('seo-h1');
		if (!seoH1) return stopSeoH1Watch();
		if (document.querySelectorAll('h1').length > 1) {
			seoH1.remove();
			stopSeoH1Watch();
		}
	};

	const addsH1 = (records: MutationRecord[]) =>
		records.some((record) =>
			Array.from(record.addedNodes).some(
				(node) =>
					node instanceof HTMLElement && (node.tagName === 'H1' || !!node.querySelector('h1'))
			)
		);

	const watchSeoH1 = () => {
		if (!browser) return;
		stopSeoH1Watch();
		pruneSeoH1();
		if (!document.getElementById('seo-h1')) return;
		seoH1Observer = new MutationObserver((records) => {
			if (addsH1(records)) pruneSeoH1();
		});
		seoH1Observer.observe(document.body, { childList: true, subtree: true });
		// Async markdown resolves long before this; the timer only stops an idle observer leaking.
		seoH1WatchTimer = setTimeout(stopSeoH1Watch, SEO_H1_WATCH_MS);
	};

	// Drop the initial-route SEO block before the first client navigation so it never leaks onto
	// the next page. Crawlers only see the initial load, so this never strips content from them.
	beforeNavigate(() => {
		document.getElementById('seo-static-block')?.remove();
	});

	afterNavigate(() => {
		const currentUrl = get(page).url;
		const pathname = currentUrl.pathname;
		syncLocaleFromPathname(pathname);
		handleModalRouteNavigation(currentUrl, base);
		const langMatch = pathname.match(/^\/([a-z]{2})(\/|$)/);
		document.documentElement.lang = langMatch?.[1] || LANGUAGE_DEFAULT || 'en';
		if (innerPageElement) {
			innerPageElement.scrollTo({
				top: 0,
				behavior: 'smooth'
			});
		}
		// Re-run h1 cleanup after JS navigations (e.g. /faq/ → /faq/withdrawals/ redirect).
		// onMount only fires once, so navigated-to pages that add their own h1 need a second check.
		watchSeoH1();
	});

	onMount(() => {
		const stopModalRouteDismissSync = initModalRouteDismissSync(() => get(page).url, base);
		handleModalRouteNavigation(get(page).url, base);

		// Safety net for routes that render no SeoText to consume the block: a logged-in user
		// should never keep it around.
		if (get(loggedIn)) {
			document.getElementById('seo-static-block')?.remove();
		}

		// Remove the injected sr-only h1 only when the rendered component provides its own h1.
		// Pages with no component h1 (category pages, etc.) keep the sr-only one as the sole heading.
		watchSeoH1();

		// Set lang at initial hydration - covers English HTML served at non-English locale paths.
		// afterNavigate handles subsequent navigations; this fires before it on first mount.
		const initPathname = get(page).url.pathname;
		const initLangMatch = initPathname.match(/^\/([a-z]{2})(\/|$)/);
		document.documentElement.lang = initLangMatch?.[1] || LANGUAGE_DEFAULT || 'en';

		if (window.top && window !== window.top) {
			window.top.location = window.location;
		}

		setAppViewportHeight();
		requestAnimationFrame(setAppViewportHeight);
		setTimeout(setAppViewportHeight, 150);
		window.addEventListener('resize', setAppViewportHeight);
		window.addEventListener('orientationchange', setAppViewportHeight);
		window.visualViewport?.addEventListener('resize', setAppViewportHeight);
		window.visualViewport?.addEventListener('scroll', setAppViewportHeight);
		document.addEventListener('focusout', setAppViewportHeight);

		// Set event theme page background if active
		if (innerPageElement && isEventAssetEnabled('pageBackground')) {
			const pageBgPath = cfgEventThemeCdnPath('pagebg.png');
			if (pageBgPath) {
				innerPageElement.style.setProperty('--event-page-bg', `url('${pageBgPath}')`);
			}
		}

		getProviders().then((result) => {
			setProviders(result);
			// Re-run SEO after providers loaded for dynamic game/provider titles
			setTitleAndDescriptionForSEO($page.url.pathname, $page.data, BRAND_TITLE);
		});

		let wasInRange: boolean | null = null;

		const handleResize = () => {
			const width = window.innerWidth;

			if (width < 1024) {
				sidebarCollapsed.set(false);
				wasInRange = false;
				return;
			}

			const isInRange = width >= 1024 && width < 1280;

			if (isInRange && wasInRange !== true) {
				sidebarCollapsed.set(true);
			} else if (!isInRange && wasInRange === true && width >= 1280) {
				sidebarCollapsed.set(false);
			}

			wasInRange = isInRange;
		};

		handleResize();

		window.addEventListener('resize', handleResize);

		return () => {
			stopModalRouteDismissSync();
			stopSeoH1Watch();
			window.removeEventListener('resize', setAppViewportHeight);
			window.removeEventListener('orientationchange', setAppViewportHeight);
			window.visualViewport?.removeEventListener('resize', setAppViewportHeight);
			window.visualViewport?.removeEventListener('scroll', setAppViewportHeight);
			document.removeEventListener('focusout', setAppViewportHeight);
			window.removeEventListener('resize', handleResize);
		};
	});

	$: {
		if (!showSidebarMobile) {
			stateShowSidebarMobile.set(false);
		}
	}
	$: canonicalUrl = (() => {
		const p = $page.url.pathname;
		// Strip locale prefix for game/provider pages - canonical is always the unprefixed /p/ URL
		const localeGameMatch = p.match(
			/^\/(?:en|cs|de|es|fr|it|ja|ko|pt|ru|zh)(\/(?:p|providers)\/.+)$/
		);
		if (localeGameMatch) return new URL(localeGameMatch[1], CANONICAL_URL).href;
		// /en/* pages canonicalize to the unprefixed root only when English is the default language
		const enMatch = LANGUAGE_DEFAULT === 'en' ? p.match(/^\/en(\/.*)?$/) : null;
		const effectivePath = enMatch ? enMatch[1] || '/' : p;
		return new URL(effectivePath, CANONICAL_URL).href;
	})();
	$: hreflangPath = (() => {
		if (!hreflangEnabled) return null;
		const p = $page.url.pathname;
		const normalized = p.endsWith('/') ? p : p + '/';
		return HREFLANG_ROUTES.has(normalized) ? normalized : null;
	})();
	$: ogLocale = (() => {
		const langMatch = $page.url.pathname.match(/^\/([a-z]{2})(\/|$)/);
		const fallback = OG_LOCALE_MAP[LANGUAGE_DEFAULT] ?? 'en_US';
		return langMatch ? (OG_LOCALE_MAP[langMatch[1]] ?? fallback) : fallback;
	})();
	$: isBackdropVisibleDesktop = (!$sidebarCollapsed || playerChatOpen) && !$isMobile;
	$: isBackdropVisibleMobile = (showSidebarMobile || playerChatOpen) && $isMobile;

	$: if (browser && $seoContentReady) {
		// eslint-disable-next-line @typescript-eslint/no-unused-expressions
		$seoContent; // reactive dependency - re-runs when locale SEO content changes
		setTitleAndDescriptionForSEO($page.url.pathname, $page.data, BRAND_TITLE);
	}

	// SEO copy is logged-out only - if the user logs in, drop the static block.
	$: if (browser && $loggedIn) {
		document.getElementById('seo-static-block')?.remove();
	}

	$: isSportsRoute = $page.url.pathname.includes('/sports');
	$: isVipModernPreview =
		$page.url.pathname.includes('/vip') &&
		wantAtLeastReleaseStage(ReleaseStage.Alpha) &&
		$stateFeatureSet === FeatureSet.Preview &&
		$stateUi.vip.vipAboutStylePreview === 'modern';
	$: if (browser) {
		document.documentElement.setAttribute('data-theme', $stateTheme);
		document.documentElement.classList.toggle('sports-route', isSportsRoute);
	}
	// The shell already ships canonical/og:url/twitter:url; update them in place on navigation
	// instead of re-emitting them in <svelte:head>, which would append duplicates (ssr=false).
	$: if (browser && canonicalUrl) updateCanonicalUrl(canonicalUrl);
	$: robotsContent = NO_INDEX
		? 'noindex, nofollow'
		: isLocaleGamePage
			? 'noindex, follow'
			: 'index,follow,max-image-preview:large';
	// Same reason as canonical above: the shell emits robots on every noindex page, so emitting it
	// in <svelte:head> appended a second, contradictory tag. `updateRobots` keeps one tag and never
	// relaxes the shell's noindex.
	$: if (browser) updateRobots(robotsContent);
	$: showHeader =
		!$isFullScreenGame ||
		($isMobile &&
			$isFullScreenGame &&
			$stateUi.games.showNavigationOnMobile &&
			$selectedItemMobileMenu !== 'chat');
	$: useAlternative4 = $stateUi.layout.layoutStyle === 'alternative4';
</script>

<svelte:head>
	<!-- title, description, canonical, robots, og:type, og:image, twitter:card/image and og:site_name
	     are injected per page and brand by the build-time shell (seo-static-plugin). Re-emitting them
	     here duplicates them: ssr=false makes Svelte mount (append), not hydrate. On navigation
	     `setTitleAndDescriptionForSEO`, `updateCanonicalUrl` and `updateRobots` update the shell's
	     tags in place (creating them when the CSR fallback is served). Keep only tags the shell
	     omits and that do not depend on runtime state. -->
	<meta http-equiv="X-Frame-Options" content="DENY" />
	<meta property="og:locale" content={ogLocale} />
	{#if hreflangPath}
		<link rel="alternate" hreflang="x-default" href="{HREFLANG_GLOBAL}{hreflangPath}" />
		<link rel="alternate" hreflang="en" href="{HREFLANG_GLOBAL}{hreflangPath}" />
		<link rel="alternate" hreflang="ko" href="{HREFLANG_KR}{hreflangPath}" />
	{/if}
	<!-- og:site_name and the favicon/apple-touch links are injected per brand by the build shell
	     (and fallback.html); re-emitting them here just appends duplicates. -->
	{#if $stateUi.layout.embedWebSiteOrganizationJsonLd}
		{@html '<script type="application/ld+json">' + webSiteOrganizationLdJson() + '</script>'}
	{/if}

	{#if BETBY_SCRIPT_URL && $hasSportsLoaded}
		<script src={BETBY_SCRIPT_URL}></script>
	{/if}
</svelte:head>

<PublicNavigation />
<Fonts />

<!-- <iframe class="fullpage" src="https://www.youtube.com/embed/1w7OgIMMRc4" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen /> -->

<div class="page" class:betby-layout={$isBetbyDisplayed} class:page-alternative-4={useAlternative4}>
	{#if $stateIFrameHref && wantAtLeastReleaseStage(ReleaseStage.Gamma)}
		<Iframe />
	{/if}
	<Modals />
	{#if showHeader}
		<div class="header">
			<Header {onShowPlayerChat} />
		</div>
	{/if}
	<div
		class:h-full={$isFullScreenGame}
		class:betby-layout={$isBetbyDisplayed}
		class="outerpage"
		style={`--right-panel-width: ${
			playerChatOpen ? 'var(--chat-width)' : '0px'
		}; --sidebar-width: ${$sidebarCollapsed ? '72px' : '240px'}`}
	>
		<div
			class="sidebar"
			class:sports-route={isSportsRoute}
			class:shell-sidebar-tablet-alt-bg={$stateUi.layout.useAlternateMobileSidebarBackdrop &&
				$isMobile &&
				showSidebarMobile}
		>
			<SideBar
				{onCollapseSidebar}
				withoutTopBorder={true}
				useParentState={true}
				isCollapsed={$sidebarCollapsed}
				bind:isOpen={showSidebarMobile}
			/>
		</div>
		{#if isBackdropVisibleDesktop || isBackdropVisibleMobile}
			<div
				class="backdrop"
				on:click={() => {
					if (!$isMobile) {
						sidebarCollapsed.set(true);
						isPlayerChatOpen.set(false);
					}
				}}
			></div>
		{/if}
		<div
			class:innerpage-full={$isFullScreenGame}
			class:event-theme={isEventAssetEnabled('pageBackground')}
			class:sports-route={isSportsRoute}
			class="innerpage {$sidebarCollapsed ? 'with-sidebar-collapsed' : ''} {playerChatOpen &&
			!$sidebarCollapsed
				? 'with-notifications-open'
				: ''}"
			bind:this={innerPageElement}
		>
			{#if $stateHasSports && isSportsRoute}
				<div class="inner visible">
					<BetBy />
				</div>
			{/if}
			<!-- <iframe class="inner" src="https://www.youtube.com/embed/1w7OgIMMRc4" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen /> -->
			<slot />
			{#if !$isBetbyDisplayed && !isSportsRoute}
				<Footer />
			{/if}
		</div>

		{#if playerChatOpen}
			<div class="chat-notifications"></div>
		{/if}
	</div>
	<Background
		shellLayout
		hideTopLight={isVipModernPreview}
		customStyle={'top: calc(70px + env(safe-area-inset-top, 0px)); width: auto; height: auto;'}
	/>
	<MobileMenu
		onClickMenu={onShowSidebar}
		onClickChat={onMobileMenuChatSlotClick}
		onClickSearch={onShowSearchModal}
		onClickSupport={() => handleCustomerSupport({ forceWidget: true })}
	/>
	<ToastNotifications />
	{#if $stateFeatureSet === FeatureSet.Preview && wantAtLeastReleaseStage(ReleaseStage.Alpha) && !cfgLiveChatLicenseId()}
		<SupportChat shellEmbed />
	{/if}
	<CookiesModal />
</div>

<style lang="postcss">
	:global(body) {
		@apply [font-family:var(--font-main)];
		scroll-behavior: smooth;
	}

	:global(html.sports-route),
	:global(html.sports-route body) {
		overscroll-behavior: none;
	}

	:global(.fixed-loading-screen) {
		position: fixed !important;
		z-index: 64000 !important;
	}

	.header {
		@apply fixed z-[60] top-0 left-0 right-0;
		padding-top: env(safe-area-inset-top);
		padding-left: env(safe-area-inset-left);
		padding-right: env(safe-area-inset-right);
	}

	.outerpage {
		@apply flex flex-row w-full overflow-hidden;
		height: calc(
			var(--app-vh, 100vh) - 72px - env(safe-area-inset-top) - env(safe-area-inset-bottom, 0px)
		);
		height: calc(100dvh - 72px - env(safe-area-inset-top) - env(safe-area-inset-bottom, 0px));
		margin-top: calc(72px + env(safe-area-inset-top));
	}
	.outerpage.betby-layout {
		overflow: visible;
		overscroll-behavior: none;
	}

	@media (max-width: 1023px) {
		.outerpage.betby-layout {
			margin-top: 0;
			height: var(--app-vh, 100vh);
			height: 100dvh;
		}
	}

	.sidebar {
		@apply static w-fit z-40 h-full;
	}

	@media (max-width: 1023px) {
		.page .sidebar {
			@apply fixed;
			top: 0;
			left: 0;
			width: 0;
		}
	}

	@media (min-width: 1024px) {
		.page .sidebar.sports-route {
			@apply fixed;
			top: calc(72px + env(safe-area-inset-top));
			left: 0;
			width: var(--sidebar-width, 0px);
			height: calc(
				100dvh - 72px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)
			);
		}
	}

	.shell-sidebar-tablet-alt-bg {
		@apply bg-[var(--color-background-900)];
	}

	.chat-notifications {
		@apply static z-40 h-full;
		width: var(--right-panel-width, 0px);
	}

	@media (max-width: 1023px) {
		.page .chat-notifications {
			@apply fixed;
			width: 0;
		}
	}

	.page {
		@apply flex flex-col z-10 h-screen w-full overflow-hidden;
		height: 100dvh;
	}
	.page.betby-layout {
		overflow: visible;
		overscroll-behavior: none;
	}

	.innerpage {
		@apply relative w-full z-10 flex flex-col justify-between;
		width: 100%;
		overflow-y: auto;
		overflow-x: hidden;
		align-items: center;

		@media (min-width: 1024px) {
			/* Only apply calculation on desktop */
			width: calc(100% - var(--sidebar-width, 0px) - var(--right-panel-width, 0px));
		}

		&::-webkit-scrollbar {
			@apply w-[8px];
		}

		&::-webkit-scrollbar-track {
			@apply bg-transparent;
		}

		&::-webkit-scrollbar-thumb {
			@apply bg-[var(--color-secondary-500)] rounded-full;
		}
	}

	.innerpage.sports-route {
		overflow: visible;
		overscroll-behavior: none;
	}

	@media (min-width: 1024px) {
		.innerpage.sports-route {
			margin-left: var(--sidebar-width, 0px);
			width: calc(100% - var(--sidebar-width, 0px) - var(--right-panel-width, 0px));
		}
	}

	.innerpage.event-theme::before {
		content: '';
		position: fixed;
		top: calc(72px + env(safe-area-inset-top, 0px));
		left: 0;
		right: 0;
		bottom: 0;
		width: 100%;
		height: calc(
			var(--app-vh, 100vh) - 72px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)
		);
		height: calc(100dvh - 72px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px));
		background-image: var(--event-page-bg);
		background-size: auto;
		background-position: top left;
		background-repeat: repeat;
		opacity: 0.05;
		z-index: 0;
		pointer-events: none;
	}

	.innerpage-full {
		@apply w-full;
	}

	.with-sidebar-collapsed {
		--sidebar-width: 72px;
	}

	.fullpage {
		@apply w-full h-full absolute bottom-0 right-0 top-0 left-0 z-40;
	}

	.inner {
		@apply w-full min-w-0 max-w-full min-h-[calc(100vh-64px)] relative bottom-0 right-0 top-0 left-0 z-40 flex flex-col;
	}

	.hidden {
		@apply invisible h-0 min-h-0 overflow-hidden;
	}

	.none {
		@apply hidden;
	}

	.h-full {
		@apply !h-screen !mt-0;
	}

	.page .backdrop {
		@apply fixed inset-0 z-30 bg-black bg-opacity-50 w-full h-full;
		display: none;
	}

	@screen md {
		@media (max-width: 1279px) {
			.page .innerpage {
				@apply w-full;
			}

			.page .sidebar {
				@apply fixed;
			}

			.page .backdrop {
				@apply block;
			}

			.page .chat-notifications {
				@apply fixed;
			}

			.page .with-notifications-open {
				--right-panel-width: 0;
			}
		}
	}

	@screen lg {
		@media (max-width: 1279px) {
			.page .innerpage {
				@apply ml-[72px];
			}
		}
	}

	.page-alternative-4 {
		background: var(--component-layout-body-bg);
	}
</style>
